"use client"

import type React from "react"
import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileUp, Download, FileText, Loader2, AlertCircle, RotateCw } from "lucide-react"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { API_URL, API_KEY } from "@/lib/api-config"

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024 // 100MB

type RotationAngle = 90 | 180 | 270
type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

const rotationOptions: { value: RotationAngle; label: string }[] = [
  { value: 90, label: "90° Clockwise" },
  { value: 180, label: "180° (Flip)" },
  { value: 270, label: "90° Counter-clockwise" },
]

export function PdfRotate() {
  const [file, setFile] = useState<File | null>(null)
  const [rotationAngle, setRotationAngle] = useState<RotationAngle>(90)
  const [pageRange, setPageRange] = useState("")
  const [outputFileName, setOutputFileName] = useState("rotated")

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [resultInfo, setResultInfo] = useState<{ fileName: string; pageCount: number; size: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (selectedFile.type !== "application/pdf" && !selectedFile.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file")
      return
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB limit`)
      return
    }

    setFile(selectedFile)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)

    const baseName = selectedFile.name.replace(/\.pdf$/i, "")
    setOutputFileName(`${baseName}-rotated`)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  const clearFile = useCallback(() => {
    setFile(null)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)
    setPageRange("")
    setOutputFileName("rotated")
  }, [])

  const rotatePdf = async () => {
    if (!file) {
      toast.error("Please select a PDF file")
      return
    }

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      // Step 1: Create job and get upload URL
      const createResponse = await fetch(`${API_URL}/api/v1/image/pdf/rotate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSizeBytes: file.size,
          rotationAngle,
          pageRange: pageRange.trim() || null,
          outputFileName,
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create rotate job")
      }

      const { jobId, uploadUrl } = await createResponse.json() as { jobId: string; uploadUrl: string }

      // Step 2: Upload PDF
      setStatus("uploading")

      const xhr = new XMLHttpRequest()
      await new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress((e.loaded / e.total) * 100)
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error("Upload failed"))
          }
        }
        xhr.onerror = () => reject(new Error("Upload failed"))
        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", "application/pdf")
        xhr.send(file)
      })

      // Step 3: Process PDF
      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/pdf/rotate/${jobId}/process`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to rotate PDF")
      }

      const result = await processResponse.json() as {
        downloadUrl: string
        fileName: string
        pageCount: number
        fileSizeBytes: number
      }

      setDownloadUrl(result.downloadUrl)
      setResultInfo({
        fileName: result.fileName,
        pageCount: result.pageCount,
        size: result.fileSizeBytes,
      })
      setStatus("completed")
      toast.success(`PDF rotated successfully!`)
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadPdf = () => {
    if (!downloadUrl) return
    window.open(downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCw className="h-5 w-5" />
          PDF Rotate Pages
        </CardTitle>
        <CardDescription>
          Rotate all pages or specific pages of a PDF document.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Input */}
        <div className="space-y-4">
          <Input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="pdf-rotate-upload"
            disabled={isProcessing}
          />

          {!file ? (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 border-dashed"
              disabled={isProcessing}
            >
              <div className="flex flex-col items-center gap-2">
                <FileUp className="h-8 w-8" />
                <span>Select PDF File</span>
                <span className="text-xs text-muted-foreground">Max {MAX_FILE_SIZE_BYTES / 1024 / 1024}MB</span>
              </div>
            </Button>
          ) : (
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file.name}</p>
                <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFile} disabled={isProcessing}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Options */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="rotation-angle">Rotation Angle</Label>
            <Select
              value={rotationAngle.toString()}
              onValueChange={(v) => setRotationAngle(Number(v) as RotationAngle)}
              disabled={isProcessing}
            >
              <SelectTrigger id="rotation-angle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rotationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="page-range">Page Range (Optional)</Label>
            <Input
              id="page-range"
              value={pageRange}
              onChange={(e) => setPageRange(e.target.value)}
              placeholder="Leave empty for all pages"
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">
              e.g., 1-5, 8, 10-12 (empty = all pages)
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="output-name">Output Filename</Label>
          <div className="flex items-center gap-2">
            <Input
              id="output-name"
              value={outputFileName}
              onChange={(e) => setOutputFileName(e.target.value)}
              placeholder="rotated"
              disabled={isProcessing}
            />
            <span className="text-muted-foreground">.pdf</span>
          </div>
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                {status === "creating-job" && "Creating rotate job..."}
                {status === "uploading" && `Uploading PDF... ${Math.round(uploadProgress)}%`}
                {status === "processing" && "Rotating pages..."}
              </span>
            </div>
            {status === "uploading" && <Progress value={uploadProgress} />}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Result */}
        {status === "completed" && resultInfo && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">PDF Rotated Successfully!</p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {resultInfo.fileName} - {resultInfo.pageCount} pages - {formatFileSize(resultInfo.size)}
                </p>
              </div>
              <Button onClick={downloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        )}

        {/* Rotate Button */}
        <Button
          onClick={rotatePdf}
          disabled={!file || isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <RotateCw className="h-4 w-4 mr-2" />
              Rotate PDF
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
